import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

/**
 * Custom hook for managing onboarding mode based on user authentication state and role
 * 
 * Core Strategy: Behalf-mode is automatically determined by user authentication state and role.
 * Key Principle: isBehalfMode = isAuthenticated && hasAccountantRole
 * 
 * @returns {Object} Onboarding mode configuration
 */
export const useOnboardingMode = () => {
  const { isAuthenticated, role, user } = useAuth();
  const navigate = useNavigate();
  const [accessError, setAccessError] = useState(null);

  // Determine behalf mode based on user role
  const isBehalfMode = useMemo(() => {
    if (!isAuthenticated) return false;
    
    const accountantRoles = ['SUPERVISOR', 'USERADMIN', 'CUSTOMERADMIN', 'Supervisor', 'UserAdmin', 'CustomerAdmin'];
    return accountantRoles.includes(role);
  }, [isAuthenticated, role]);

  // Enhanced validation and error handling
  useEffect(() => {
    const pathname = window.location.pathname;
    
    // Clear any existing access errors
    setAccessError(null);

    // Check if user is trying to access behalf-mode features
    const isAccessingBehalfFeatures = pathname.includes('/client-onboardings');
    
    if (isAccessingBehalfFeatures) {
      if (!isAuthenticated) {
        // User not authenticated, redirect to login
        setAccessError('Authentication required');
        navigate('/login');
        return;
      }
      
      if (!isBehalfMode) {
        // User authenticated but doesn't have behalf access
        setAccessError('Insufficient permissions for client management');
        console.warn('User attempted to access behalf-mode features without proper role:', {
          role,
          isAuthenticated,
          pathname
        });
        
        // Redirect to appropriate page based on user type
        if (user?.creditors?.length > 0) {
          navigate('/Overview');
        } else {
          navigate('/WelcomeNoAccess');
        }
        return;
      }
    }

    // Additional validation for onboarding setup with submissionId
    if (pathname.includes('/onboarding/setup')) {
      const urlParams = new URLSearchParams(window.location.search);
      const submissionId = urlParams.get('submissionId');
      
      if (submissionId && !isBehalfMode) {
        // User trying to access a client onboarding setup without proper role
        setAccessError('Insufficient permissions for client onboarding management');
        console.warn('User attempted to access client onboarding setup without proper role:', {
          role,
          isAuthenticated,
          submissionId,
          pathname
        });
        navigate('/Overview');
        return;
      }
    }
  }, [isBehalfMode, isAuthenticated, navigate, role, user]);

  // Validate role consistency
  const validateRoleAccess = (requiredRoles = ['SUPERVISOR', 'USERADMIN', 'CUSTOMERADMIN', 'Supervisor', 'UserAdmin', 'CustomerAdmin']) => {
    if (!isAuthenticated) {
      return {
        hasAccess: false,
        error: 'User not authenticated'
      };
    }

    if (!requiredRoles.includes(role)) {
      return {
        hasAccess: false,
        error: `Insufficient role. Required: ${requiredRoles.join(', ')}, Current: ${role}`
      };
    }

    return {
      hasAccess: true,
      error: null
    };
  };

  return {
    isBehalfMode,
    skipEmailVerification: isBehalfMode,
    skipSignature: isBehalfMode,
    userRole: role,
    user,
    accessError,
    validateRoleAccess,
    // Helper functions for specific role checks
    canManageClients: isBehalfMode,
    canCreateClientOnboarding: isBehalfMode,
    canAccessClientData: isBehalfMode
  };
};